import { useMemo } from "react";
import { einheitFuer, einheitImSatz, useT, type Uebersetzer } from "../i18n";
import { Erklaerung, Marke, euro } from "./index";
import { Zeichen } from "./Zeichen";
import { EINHEITSWORT, tagMitWochentag, zeitraumZeile } from "./preistext";
import {
  Abschnitt, Leersatz, Wertzeile, Zeilenblatt, Zweizeiler,
} from "./Handyformen";
import { useTelefon } from "../schale/geraet";
import type { Geraetelage, Typenlage } from "../daten/nachschlagen";
import type { Buendelpreis, Schritt } from "../daten/preisbuendel";
import type { Maschinentyp, Tarif } from "../daten/typen";
/* Die Telefonform der Auskunft (`.tat-*`) — sie steht im Nachschlagen und
   beim Vermieten, und die Datei wird mit beiden geladen; hier noch einmal,
   damit der Baustein seine Form nicht von seinem Aufrufer borgt. */
import "../stile/telefon/nachschlagen.css";
import { vormerkungWer } from "../daten/reserviertsatz";
import { satzMalSchluessel } from "../daten/einmalbetrag";

/* ══════════════════════════════════════════════════════════════════
   Was unter einer aufgeklappten Trefferzeile steht.

   Luca, 17.09.2026: *„Auch wenn ich dann die einzelnen Positionen in
   der Suche anklicke, muss da auch dann die Zuschläge und so weiter
   sauber stehen."* — und einen Tag vorher, zur Preisauskunft: *„Bei der
   Preisliste ist es sehr sinnvoll, dass wir die Staffelpreise da auch
   richtig anzeigen."*

   Beides steht jetzt an derselben Stelle, und das ist der Punkt dieser
   Datei: **Die Vermietfläche und das Nachschlagen zeigen dieselbe
   Auskunft.** Sie waren bis zum 17.09.2026 zwei Masken mit zwei
   Aufklappungen — die eine hatte die Staffel, die andere die Stücke.
   Wer beides brauchte, wechselte die Maske.

   *Nachtrag vom selben Abend:* Dieser Satz stimmte einen Tag lang nur
   halb. Das Blatt nahm den Baustein, die Fläche klappte weiter mit
   eigenem JSX auf — ohne Ausstattungshinweis und ohne Staffel,
   ausgerechnet dort, wo vermietet wird. Ein Kommentar, der eine Zusage
   macht, die der Code nicht hält, ist teurer als die fehlende Staffel:
   Wer ihn liest, baut darauf. Seit `masken/Vermieten.tsx` hier
   hereingreift, stimmt er.

   Vier Teile, jeder nur dort, wo er etwas sagt:

     · **Der Rechenweg** — Schlüssel mit Zahlen aus der Preisfindung, kein
       Satz, den die Maske erfindet. Wer am Telefon einen Preis nennt,
       muss sagen können, wie er zustande kommt.
     · **Die Staffel** — die Stufen, wie der Betrieb sie führt, mit der
       hervorgehoben, die für den gefragten Zeitraum greift. Eine Stufe
       ist keine Staffel; dann steht hier nichts.
     · **Die Zuschläge** — am Schreibtisch Zeilen derselben Aufstellung wie
       die Miete (`AuskunftTafel`, seit dem 08.10.2026), zum Lesen: Eine
       Auskunft ändert keinen Preis.
     · **Die Stücke** — der Streifen sagt „zwei von drei belegt", das
       Aufklappen sagt, welche zwei.

   Klassen: `ta-*` (Schreibtisch) und `tat-*` (Telefon), gegen alle Stile
   geprüft.
   ══════════════════════════════════════════════════════════════════ */

export function Trefferauskunft({ lage, preis, typ, ohneVariante = false }: {
  lage: Typenlage;
  /** Der Preis im gefragten Zeitraum. Fehlt er, bleiben Rechenweg und Staffel weg. */
  preis: Buendelpreis | undefined;
  /** Der Typ aus dem Stamm — nur für die Staffel. Ohne ihn gibt es keine. */
  typ: Maschinentyp | undefined;
  /**
   * **„Gerechnet mit …" steht schon über mir** (18.09.2026).
   *
   * Im Nachschlagen am Telefon steht die Ausstattung als Griff über der
   * Auskunft, weil man sie dort ändern kann (`masken/NachschlagenTelefon.tsx`,
   * „Gerechnet mit"). Zweimal derselbe Wert ist kein zweiter Hinweis, sondern
   * eine Zeile weniger Antwort. Am Schreibtisch und beim Vermieten steht
   * nichts darüber — dort bleibt die Zeile, und deshalb ist das eine Angabe
   * des Aufrufers und keine Regel dieser Datei.
   */
  ohneVariante?: boolean;
}) {
  const telefon = useTelefon();

  /* **Gezeigt wird, was gepflegt ist, und nichts sonst.** Die Sätze kommen
     aus `wirksam.tarife` — also aus derselben Kette, mit der `GET /preise`
     rechnet, samt geerbter Gruppenpreise. Wo nur eine Stufe steht, gibt es
     keine Staffel; Stufen zu erfinden wäre eine Preisliste mit falschen
     Zahlen.

     **Die Stufen der gerechneten Ausstattung**, nicht irgendwelche: Eine
     Stufe, die nur „mit Anschläger" gilt, gehört nicht unter einen Preis,
     der ohne gerechnet wurde. */
  const staffel = useMemo(() => {
    const fuer = preis?.varianteId ?? "";
    const roh = (typ?.wirksam.tarife ?? [])
      .filter((r) => (r.variante_id ?? "") === fuer && r.netto !== null)
      .sort((a, b) => a.ab_tag - b.ab_tag);
    return roh.length > 1 ? roh : [];
  }, [typ, preis]);

  /** Welche Stufe für den gefragten Zeitraum greift — die letzte, die
   *  angefangen hat. Ohne sie wäre die Liste richtig und die Auskunft
   *  trotzdem nicht beantwortet. */
  const stufeJetzt = useMemo(() => {
    if (!staffel.length || !preis) return null;
    const greift = staffel.filter((r) => r.ab_tag <= preis.miettage);
    return (greift[greift.length - 1] ?? staffel[0])?.id ?? null;
  }, [staffel, preis]);

  const zuschlaege = (preis?.zuschlaege ?? []).filter((z) => z.vorgewaehlt);
  const schritte = (preis?.schritte ?? []).filter((s) => s.key !== "preis.netto");

  /* **Am Telefon dieselbe Auskunft in der Sprache der Handyfassung**
     (19.09.2026) — dieselben Zahlen aus derselben Antwort, nur gesetzt aus
     `Abschnitt`, `Zeilenblatt` und `Wertzeile` statt aus Zeilen für einen
     Schirm mit Zeiger. Siehe `AuskunftTelefon` unten. */
  if (telefon) {
    return (
      <AuskunftTelefon
        lage={lage} preis={preis} typ={typ} ohneVariante={ohneVariante}
        staffel={staffel} stufeJetzt={stufeJetzt} zuschlaege={zuschlaege} schritte={schritte}
      />
    );
  }

  return (
    <AuskunftTafel
      lage={lage} preis={preis} typ={typ} ohneVariante={ohneVariante}
      staffel={staffel} stufeJetzt={stufeJetzt} zuschlaege={zuschlaege} schritte={schritte}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════
   Die Auskunft am Schreibtisch — eine Aufstellung, die man nachrechnet
   (Paket 9, „Vermieten, Auskunft", 08.10.2026).

   Luca: *„Wenn ich das aufklicke, finde ich das optisch nicht schön
   formatiert. Die Staffelung, dann ‚aus dem Stamm gerechnet',
   Maschinenbruchversicherung — das muss alles formatiert sein."*

   Bis dahin standen hier vier Formen untereinander: der Rechenweg als
   Fließtext, die Staffel als Rasterzeilen, die Zuschläge mit ihrer
   Grundlage in der Mitte und die Summe darunter — **ohne die Miete**. Aus
   „52,50 + 15,00 = 367,50" ließ sich nichts nachrechnen.

   Jetzt ist es **eine Tabelle mit festen Spalten** — Bezeichnung · Menge ·
   Einzel · Betrag —, deren erste Zeile die Miete ist. Die drei Zahlenspalten
   sind so breit wie Lage, Miettage und Preis der Gerätezeile darüber
   (`--tr-lage`, `--tr-tage`, `--tr-betrag`) und enden an derselben Kante:
   Der Betrag der Miete steht genau unter dem Preis der Zeile.

   **Keine Zahl ist neu und keine anders.** Menge, Einzel und Betrag der
   Miete sind `menge`, `einzelpreis` und `netto` der Preisfindung — dieselben
   Werte, die am Telefon als „Satz" und „Miete" stehen. Der Rechenweg steht
   darunter in kurzen Worten: Was die Spalten schon sagen („5 Miettage im
   Zeitraum", „Staffel ab Tag 3 · 180") fällt weg, was nach der Miete kommt
   (Abrechnung nach Betriebsstunden oder Protokoll), steht als Hinweis unter
   der Summe. Die Summe rechnet wie die Zuschlagsanzeige: Miete plus jeder
   Zuschlag, auf den Cent gerundet.

   Klassen: `ta-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Schritte, die nach der Miete gelten — sie stehen unter der Summe, nicht im Rechenweg. */
const HINWEISE = new Set(["preis.je_miettag.abrechnung", "preis.abrechnung_nach_stunden"]);
/** Schritte, die eine Spalte schon sagt: Menge (Miettage) und Einzel (die greifende Stufe). */
const SCHON_IN_SPALTE = new Set(["preis.miettage", "preis.staffel"]);
/** Schritte, die es in kurzen Worten gibt — die Zahlen dazu stehen in den Spalten. */
const KURZ: Record<string, string> = {
  "preis.je_miettag.mindestmiete": "nachschlagen.auskunft.kurzMindestmiete",
  "preis.fixpreis": "nachschlagen.auskunft.kurzFixpreis",
};
/** Werte eines Schritts, die Beträge sind. */
const BETRAGSWERTE = new Set(["satz", "betrag", "vorher"]);

/**
 * Die Werte eines Schritts lesbar — Beträge mit zwei Stellen, Zahlen mit
 * Komma. Die Preisfindung schickt Zahlen („15", „2.5"); bis hierher standen
 * sie roh im Satz. Gezeigt wird dieselbe Zahl, nur geschrieben, wie die
 * Spalten sie schreiben.
 */
function schrittWerte(s: Schritt): Record<string, unknown> {
  const raus: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(s)) {
    if (k === "key" || typeof v !== "number") { raus[k] = v; continue; }
    /* Plural braucht die Zahl selbst („# Betriebsstunden"); nur Beträge
       werden zu Text — sie stehen nie in einer Plural-Form. */
    raus[k] = BETRAGSWERTE.has(k) ? euro(v) : v;
  }
  return raus;
}

/** Eine Menge mit ihrer Einheit, wie in den Positionen: „5 Tage", „1 Pauschale". */
function mengeMitEinheit(t: Uebersetzer, menge: number, einheitKey: string): string {
  const zahl = menge.toLocaleString("de-DE", { maximumFractionDigits: 2 });
  return einheitKey ? `${zahl} ${einheitFuer(t, einheitKey, menge)}` : zahl;
}

function AuskunftTafel({
  lage, preis, typ, ohneVariante, staffel, stufeJetzt, zuschlaege, schritte,
}: {
  lage: Typenlage;
  preis: Buendelpreis | undefined;
  typ: Maschinentyp | undefined;
  ohneVariante: boolean;
  staffel: Tarif[];
  stufeJetzt: string | null;
  zuschlaege: Buendelpreis["zuschlaege"];
  schritte: Buendelpreis["schritte"];
}) {
  const t = useT();
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const hatPreis = Boolean(preis && !preis.ohnePreis);
  const variante = preis?.varianteId && !ohneVariante
    ? typ?.varianten.find((v) => v.id === preis.varianteId)?.label ?? ""
    : "";

  /* Der Rechenweg in kurzen Worten — und was erst nach der Miete gilt. */
  const weg: string[] = [];
  const hinweise: string[] = [];
  if (variante) weg.push(t("nachschlagen.gerechnetMit", { variante }));
  for (const s of schritte) {
    /* Die greifende Stufe: Gibt es eine Staffel, verweist ein kurzes Wort
       auf sie (die Zahl steht in „Einzel" und markiert in der Staffel);
       gibt es keine, sagt „Einzel" schon alles. */
    if (s.key === "preis.staffel" && staffel.length) {
      weg.push(t("nachschlagen.auskunft.kurzStaffel", { ab_tag: Number(s["ab_tag"]) }));
      continue;
    }
    if (SCHON_IN_SPALTE.has(s.key)) continue;
    const werte = schrittWerte(s);
    if (HINWEISE.has(s.key)) { hinweise.push(t(s.key, werte as never)); continue; }
    weg.push(t(KURZ[s.key] ?? s.key, werte as never));
  }

  /* Dieselbe Summe wie in der Zuschlagsanzeige: Miete plus jeder Zuschlag,
     auf den Cent. Ein Zuschlag ohne Preis zählt mit seinem Betrag (0) —
     und steht als „zzgl." darunter. */
  const miete = preis?.netto ?? 0;
  const summe = Math.round((miete + zuschlaege.reduce((s, z) => s + z.netto, 0)) * 100) / 100;
  const offen = zuschlaege.filter((z) => z.preisIndividuell).map((z) => z.bezeichnung);
  const mitSumme = hatPreis && (zuschlaege.length > 0 || preis?.zuschlagsfrei === true);

  return (
    <div className="ta">
      <div className="ta-tafel" role="table" aria-label={t("nachschlagen.auskunft.titel", { typ: lage.typ })}>
        <div className="ta-zeile ta-kopf" role="row">
          <span role="columnheader">{t("nachschlagen.auskunft.spalteWas")}</span>
          <span role="columnheader">{t("nachschlagen.auskunft.spalteMenge")}</span>
          <span role="columnheader">{t("nachschlagen.auskunft.spalteEinzel")}</span>
          <span role="columnheader">{t("nachschlagen.auskunft.spalteBetrag")}</span>
        </div>

        {/* Die Miete selbst — die erste Zahl, die am Telefon genannt wird. */}
        <div className="ta-zeile ta-miete" role="row">
          <span className="ta-was" role="rowheader">
            <span className="ta-name">{t("nachschlagen.auskunft.miete", { typ: lage.typ })}</span>
            {hatPreis && weg.length ? <span className="ta-neben">{weg.join(trenn)}</span> : null}
          </span>
          <span className="ta-zahl" role="cell">
            {preis && hatPreis ? mengeMitEinheit(t, preis.menge, preis.einheitKey) : ""}
          </span>
          <span className="ta-zahl" role="cell">{preis && hatPreis ? euro(preis.einzelpreis) : ""}</span>
          <span className={`ta-zahl ta-betrag${hatPreis ? "" : " still"}`} role="cell">
            {preis && hatPreis
              ? euro(preis.netto)
              : preis
                ? (preis.ohnePreis === "kein_tarif" ? t("vermieten.keinTarif") : t("allgemein.strich"))
                : t("allgemein.laedt")}
          </span>
        </div>

        {zuschlaege.length || preis?.zuschlagsfrei ? (
          <div className="ta-gruppe" role="row">
            <span role="cell">
              {t("nachschlagen.auskunft.zuschlaege")}
              <Erklaerung k="start.preisAusStammWas" />
            </span>
          </div>
        ) : null}

        {/* Auf diesen Preis schlägt bewusst nichts auf (0144) — gesagt, nicht weggelassen. */}
        {preis?.zuschlagsfrei ? (
          <div className="ta-zeile" role="row">
            <span className="ta-was" role="rowheader">
              <span className="ta-name">
                {t("start.preisOhneZuschlaege")}
                <Erklaerung k="start.preisOhneZuschlaegeWarum" />
              </span>
            </span>
            <span role="cell" />
            <span role="cell" />
            <span className="ta-zahl ta-betrag" role="cell">{euro(0)}</span>
          </div>
        ) : null}

        {zuschlaege.map((z) => (
          <div className="ta-zeile" role="row" key={z.code}>
            <span className="ta-was" role="rowheader">
              <span className="ta-name">{z.bezeichnung}</span>
              {/* Was über den Betrag zu wissen ist, steht leise darunter —
                  einmal, und das Wort trägt es, nicht die Farbe allein. */}
              {z.preisIndividuell ? (
                <span className="ta-neben">{t("zuschlaege.preisIndividuell")}</span>
              ) : z.platzhalter ? (
                <span className="ta-neben warnt" title={t("preise.platzhalterTitel")}>{t("preise.platzhalter")}</span>
              ) : null}
            </span>
            <span className="ta-zahl" role="cell">
              {z.einheitKey ? mengeMitEinheit(t, z.menge, z.einheitKey) : ""}
            </span>
            <span className="ta-zahl" role="cell">{z.preisIndividuell ? "" : euro(z.einzelpreis)}</span>
            <span className={`ta-zahl ta-betrag${z.platzhalter ? " warnt" : ""}`} role="cell">
              {z.preisIndividuell ? <Marke art="stopp">{t("zuschlaege.preisFehlt")}</Marke> : euro(z.netto)}
            </span>
          </div>
        ))}

        {mitSumme ? (
          <div className="ta-zeile ta-summe" role="row">
            <span className="ta-was" role="rowheader">
              <span className="ta-name">{t("start.preisMitZuschlaegen")}</span>
              {offen.length ? (
                <span className="ta-neben">{t("nachschlagen.auskunft.zzgl", { was: offen.join(", ") })}</span>
              ) : null}
            </span>
            <span role="cell" />
            <span role="cell" />
            <span className="ta-zahl ta-betrag" role="cell">{euro(summe)}</span>
          </div>
        ) : null}
      </div>

      {/* Wie nach der Miete abgerechnet wird — ein eigener Satz, nicht Teil des Rechenwegs. */}
      {hatPreis && hinweise.length ? (
        <ul className="ta-hinweise">
          {hinweise.map((h) => (
            <li key={h}><Zeichen name="info" /><span>{h}</span></li>
          ))}
        </ul>
      ) : null}

      {/* Die Staffel als kleine Tabelle: je Stufe eine Spalte, die greifende
          in `--gewaehlt`. Eine Stufe ist keine Staffel. */}
      {staffel.length ? (
        <div className="ta-staffel">
          <span className="ta-schild">
            {t("start.preisStaffel")}
            <Erklaerung k="start.preisStaffelWas" />
          </span>
          <table className="ta-stufen">
            <thead>
              <tr>
                {staffel.map((r) => (
                  <th scope="col" key={r.id} className={r.id === stufeJetzt ? "jetzt" : undefined}>
                    {t("preise.stufeAbTag", { tag: r.ab_tag })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {staffel.map((r) => (
                  <td
                    key={r.id}
                    className={r.id === stufeJetzt ? "jetzt" : undefined}
                    {...(r.id === stufeJetzt ? { "aria-current": "true" as const } : {})}
                  >
                    {t("start.preisStufeSatz", {
                      satz: euro(r.netto),
                      einheit: t(EINHEITSWORT[r.einheit] ?? "allgemein.strich"),
                    })}
                    {r.id === stufeJetzt ? <span className="nur-vorleser">{` ${t("start.preisStufeJetzt")}`}</span> : null}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Die Stücke — die Zeile sagt „eins von drei frei", hier steht, welches. */}
      {lage.geraete.length ? (
        <div className="ta-stuecke">
          <span className="ta-schild">{t("nachschlagenTelefon.stuecke")}</span>
          <ul>
            {lage.geraete.map((g) => {
              const s = stueck(t, g);
              return (
                <li className="ta-stueck" key={g.id}>
                  <span className={`pkt ${s.punkt}`} aria-hidden="true" />
                  <span className="kennung">{g.kennung}</span>
                  <span className="ta-stueck-was">{`${t("allgemein.trennpunkt")} ${[s.was, s.ab].filter(Boolean).join(trenn)}`}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Angebot, Auftrag im Abschluss oder „wird gerade vermietet" — ohne Frist kein „bis" (N7-05). */
function wer(t: Uebersetzer, r: NonNullable<Geraetelage["reserviert"]>): string {
  const satz = vormerkungWer(r, r.frist ? tagMitWochentag(t, r.frist) : "");
  return t(satz.schluessel, satz.werte);
}

/**
 * Wie ein einzelnes Stück steht — Punkt, Satz und „frei ab", an beiden
 * Geräten dieselben Wörter (seit dem 19.09.2026 an einer Stelle, weil die
 * Telefonform sie zum zweiten Mal braucht).
 */
function stueck(t: Uebersetzer, g: Geraetelage): { punkt: "gut" | "acht" | "stopp"; was: string; ab: string } {
  const ganzFrei = g.lage === "geht" && g.tageVergeben === 0;
  /* **Ein reserviertes Stück ist frei — aber es zu nehmen ist eine
     Entscheidung** (18.09.2026). Grün wäre zu viel versprochen, rot zu
     wenig: also gelb. Bis zum 18.09.2026 stand die Vormerkung nur am
     Telefon, und dieselbe Liste sagte am Schreibtisch „frei" zu einem
     Stück, das ein Angebot hält — zwei Antworten auf dieselbe Frage. */
  const punkt = g.werkstatt
    ? "stopp"
    : g.reserviert
      ? (g.reserviert.abschlussLaeuft ? "stopp" : "acht")
      : g.lage === "geht" ? "gut" : g.lage === "teils" ? "acht" : "stopp";
  const was = g.werkstatt
    ? t("nachschlagen.werkstatt")
    : g.reserviert
      ? wer(t, g.reserviert)
      : ganzFrei
        ? t("nachschlagen.geraetFrei")
        : g.kunde
          ? t("nachschlagen.beiKunde", { kunde: g.kunde })
          : t(g.textKey);
  const ab = !g.werkstatt && g.freiAb && !ganzFrei
    ? t("nachschlagen.abKurz", { tag: tagMitWochentag(t, g.freiAb) })
    : "";
  return { punkt, was, ab };
}

/* ══════════════════════════════════════════════════════════════════
   Die Auskunft am Telefon (19.09.2026).

   Luca: *„Die Nachschlagemasken hast du auch noch nicht fertig gemacht."* —
   und: *„…das muss einheitlich sein von der Struktur."*

   **Bis hierher stand am Telefon die Fassung des Schreibtischs**, nur
   größer gesetzt: der Rechenweg in Mono über die volle Breite, die Staffel
   als Rasterzeilen, die Zuschläge in drei Spalten mit ihrer Grundlage
   darunter, die Stücke als Punkt, Kennung und Satz untereinander — vier
   Formen in einem Kasten, und keine davon die der Handyfassung.

   **Jetzt ist es dieselbe Auskunft in den Formen, die jeder andere Schirm
   am Telefon spricht** (`bausteine/Handyformen.tsx`):

   | Teil | Form | warum |
   |---|---|---|
   | Preis | `Wertzeile`n, Beträge in Mono | Beschriftung und Betrag — die Rechnung, von oben nach unten gelesen |
   | wie es zustande kommt | leise Sätze darunter | Miettage und Rechenweg sind Sätze, die man vorliest |
   | Zuschläge | `Zweizeiler` ohne Winkel | Name, Grundlage, Betrag — und der offene Ring, wo niemand den Betrag entschieden hat |
   | Staffel | `Wertzeile`n | „ab Tag 5 · 110,00 je Tag" ist ein Wertepaar |
   | Stücke | `Zweizeiler` ohne Winkel | Kennung, wie es steht, der Punkt der Lage |

   **Gerechnet wird hier nichts, und keine Zahl ist neu.** Alles steht schon
   in der Antwort (`GET /preise`, oder `GET /preis`, wo im Nachschlagen eine
   Annahme geändert wurde) — dieselben Werte, die die Schreibtischfassung
   oben zeigt. Die Beträge im Stamm sind Beispiele (CLAUDE.md); hier werden
   sie nur gezeigt.
   ══════════════════════════════════════════════════════════════════ */
function AuskunftTelefon({
  lage, preis, typ, ohneVariante, staffel, stufeJetzt, zuschlaege, schritte,
}: {
  lage: Typenlage;
  preis: Buendelpreis | undefined;
  typ: Maschinentyp | undefined;
  ohneVariante: boolean;
  staffel: Tarif[];
  stufeJetzt: string | null;
  zuschlaege: Buendelpreis["zuschlaege"];
  schritte: Buendelpreis["schritte"];
}) {
  const t = useT();
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const hatPreis = Boolean(preis && !preis.ohnePreis);
  const variante = preis?.varianteId
    ? typ?.varianten.find((v) => v.id === preis.varianteId)?.label ?? ""
    : "";
  /* Auf den Cent gerundet wie in der Zuschlagsanzeige — sonst steht unter
     derselben Rechnung einmal 1.610,00 und einmal 1.610,000000002. */
  const zuschlagsSumme = Math.round(zuschlaege.reduce((s, x) => s + x.netto, 0) * 100) / 100;
  const summe = Math.round(((preis?.netto ?? 0) + zuschlagsSumme) * 100) / 100;
  const miettage = preis && hatPreis
    ? (preis.miettage === 1
      ? t("preisauskunft.einMiettag")
      : t("preisauskunft.miettage", { zahl: preis.miettage }))
    : "";

  return (
    <div className="tat">
      <Abschnitt titel={t("nachschlagenTelefon.preis")} {...(miettage ? { auskunft: miettage } : {})}>
        {preis && hatPreis ? (
          <>
            <Zeilenblatt>
              {/* Welche Ausstattung gerechnet wurde — nur dort, wo es eine
                  ist. Ein Kran führt nur Sätze je Ausstattung; ohne diese
                  Zeile hielte jemand die Zahl für den Grundpreis. */}
              {variante && !ohneVariante ? (
                <Wertzeile was={t("positionen.ausstattung")}>{variante}</Wertzeile>
              ) : null}
              {/* Der Satz vor der Summe, weil am Telefon beides genannt wird —
                  „hundert am Tag, macht fünfhundert". */}
              <Wertzeile was={t("nachschlagenTelefon.satz")} betrag>
                {t(satzMalSchluessel(preis.einheitKey, "handypreis.grundlage", "handypreis.grundlagePauschal"), {
                  menge: preis.menge,
                  einheit: einheitImSatz(t, preis.einheitKey, preis.menge),
                  satz: euro(preis.einzelpreis),
                })}
              </Wertzeile>
              <Wertzeile was={t("nachschlagenTelefon.miete")} betrag>{euro(preis.netto)}</Wertzeile>
              {zuschlaege.length ? (
                <>
                  <Wertzeile was={t("nachschlagenTelefon.zuschlaege")} betrag>{euro(zuschlagsSumme)}</Wertzeile>
                  <Wertzeile was={t("start.preisMitZuschlaegen")} summe>{euro(summe)}</Wertzeile>
                </>
              ) : null}
            </Zeilenblatt>
            {/* Bis wann, und warum die Miettage abweichen (Birgit, 16.09.2026:
                *„Ich bin bei 8 Tagen, und da stehen 6."*) — dann der Rechenweg
                aus der Preisfindung, Schlüssel mit Zahlen und kein Satz, den
                die Maske erfindet. */}
            <ul className="tat-weg">
              <li>{zeitraumZeile(t, preis)}</li>
              {schritte.map((s, i) => <li key={`${s.key}${i}`}>{t(s.key, schrittWerte(s) as never)}</li>)}
            </ul>
          </>
        ) : (
          <Leersatz>{preis ? t("handypreis.keinPreis") : t("allgemein.laedt")}</Leersatz>
        )}
      </Abschnitt>

      {/* **Die Zuschläge einzeln, nicht als Anzahl** — wer am Telefon eine
          Zahl nennt, muss sagen können, woraus sie besteht (Luca,
          16.09.2026). Und wo bewusst nichts aufschlägt (0144), steht genau
          das: Eine fehlende Liste sähe aus wie „noch nicht gerechnet". */}
      {hatPreis && (zuschlaege.length || preis?.zuschlagsfrei) ? (
        <Abschnitt titel={t("nachschlagenTelefon.zuschlaege")} auskunft={t("start.preisAusStamm")}>
          {preis?.zuschlagsfrei ? (
            <Leersatz>
              {t("start.preisOhneZuschlaege")}
              <Erklaerung k="start.preisOhneZuschlaegeWarum" />
            </Leersatz>
          ) : (
            <Zeilenblatt>
              {zuschlaege.map((z) => (
                <Zweizeiler
                  key={z.code}
                  /* **Ein Betrag, den niemand entschieden hat, sagt es**
                     (0032/0036): der offene Ring **und** das Wort — Farbe
                     trägt nie allein (28-designkonzept.md). */
                  punkt={z.preisIndividuell ? "stopp" : z.platzhalter ? "acht" : "ruhig"}
                  titel={z.bezeichnung}
                  unter={(
                    <>
                      {/* **Das Wort steht vorn**, weil die Zeile hinten
                          abgeschnitten wird: „Nicht am Telefon nennen"
                          (`preise.platzhalterTitel`) ist die Aussage, die
                          niemand verlieren darf. */}
                      {z.platzhalter ? (
                        <><b className="tat-warnt">{t("preise.platzhalter")}</b>{trenn}</>
                      ) : null}
                      {/* **Preis immer individuell** (0227, E1 — die
                          Anlieferung): Es gibt keinen Satz, also wird keiner
                          genannt. Der Preis wird am Posten eingegeben. */}
                      {z.preisIndividuell ? (
                        <><b className="tat-warnt">{t("zuschlaege.preisIndividuell")}</b>{trenn}</>
                      ) : null}
                      {/* Die Grundlage: wie oft, zu welchem Satz. Ein Betrag,
                          dessen Grundlage fehlt, ist der Anfang einer
                          Diskussion auf der Rechnung. */}
                      {z.einheitKey && !z.preisIndividuell
                        ? t("start.preisZuschlagWie", {
                          menge: z.menge, einheit: einheitFuer(t, z.einheitKey, z.menge), satz: euro(z.einzelpreis),
                        })
                        : ""}
                    </>
                  )}
                  rechts={(
                    <span className={`tat-betrag${z.platzhalter || z.preisIndividuell ? " warnt" : ""}`}>
                      {z.preisIndividuell ? t("zuschlaege.preisFehlt") : euro(z.netto)}
                    </span>
                  )}
                  winkel={false}
                />
              ))}
            </Zeilenblatt>
          )}
        </Abschnitt>
      ) : null}

      {/* Die Staffel, wie der Betrieb sie führt — mit der Stufe, die für den
          gefragten Zeitraum greift. Eine Stufe ist keine Staffel. */}
      {staffel.length ? (
        <Abschnitt titel={t("start.preisStaffel")}>
          <Zeilenblatt>
            {staffel.map((r) => (
              <Wertzeile key={r.id} was={t("preise.stufeAbTag", { tag: r.ab_tag })} betrag>
                {r.id === stufeJetzt ? (
                  <span className="tat-gilt"><Marke>{t("start.preisStufeJetzt")}</Marke></span>
                ) : null}
                {t("start.preisStufeSatz", {
                  satz: euro(r.netto),
                  einheit: t(EINHEITSWORT[r.einheit] ?? "allgemein.strich"),
                })}
              </Wertzeile>
            ))}
          </Zeilenblatt>
        </Abschnitt>
      ) : null}

      {/* Die Stücke — die Zeile sagt „zwei von drei frei", hier steht, welche. */}
      <Abschnitt
        titel={t("nachschlagenTelefon.stuecke")}
        {...(lage.bestand ? { auskunft: t("nachschlagen.freiVon", { frei: lage.frei, bestand: lage.bestand }) } : {})}
      >
        {lage.geraete.length ? (
          <Zeilenblatt>
            {lage.geraete.map((g) => {
              const s = stueck(t, g);
              return (
                <Zweizeiler
                  key={g.id}
                  punkt={s.punkt}
                  titel={<span className="tat-kennung">{g.kennung}</span>}
                  unter={[s.was, s.ab].filter(Boolean).join(trenn)}
                  /* Eine Vormerkung ist ein Satz („Durch Angebot … bis Fr
                     25.9. reserviert") — der darf umbrechen. */
                  satz={Boolean(g.reserviert)}
                  winkel={false}
                />
              );
            })}
          </Zeilenblatt>
        ) : (
          <Leersatz>{t("lage.kein_geraet")}</Leersatz>
        )}
      </Abschnitt>
    </div>
  );
}
